import { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../Deal/Deal.css";
import { BiSolidPhoneCall } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import emailjs from "emailjs-com";
import Swal from 'sweetalert2';
import { airports } from "./FlightList"; // Importing the airports data

// Add this custom CSS to your component or import in your CSS file
const datePickerStyles = `
  .disabled-date-input {
    cursor: not-allowed !important;
    opacity: 0.6 !important;
    background-color: #f5f5f5 !important;
    pointer-events: none !important;
  }
  
  @media screen and (max-width: 767px) {
    .disabled-date-input {
      opacity: 0.4 !important;
    }
  }
`;

export default function Deal() {
  const navigate = useNavigate();
  const [isOneWay, setIsOneWay] = useState(false);
  const [depart, setDepart] = useState("London All Airport");
  const [arrive, setArrive] = useState("");
  const [departSuggestions, setDepartSuggestions] = useState([]);
  const [arriveSuggestions, setArriveSuggestions] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const [leaveDate, setLeaveDate] = useState(null); // Date object
  const [returnDate, setReturnDate] = useState(null); // Date object
  const [isMobile, setIsMobile] = useState(false);
  const [departCode, setDepartCode] = useState("LHR"); // Default for London
  const [arriveCode, setArriveCode] = useState("");
  const [isLoading, setIsLoading] = useState(false); // Set to false since we're not fetching data
  const leaveDatePickerRef = useRef(null);
  const returnDatePickerRef = useRef(null);
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    remarks: "",
    departFrom: "",
    arriveAt: "",
    leaveOn: "",
    returnOn: "",
  });

  // Process the imported airports data to match the expected format
  const processedAirports = airports.map((airportStr, index) => {
    const matches = airportStr.match(/(.*?)\s*\((.*?)\)/);
    return {
      id: index + 1,
      airportName: matches ? matches[1].trim() : airportStr,
      airportCode: matches ? matches[2].trim() : "",
    };
  });

  // Check if the device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    checkIfMobile();

    // Add event listener for window resize
    window.addEventListener("resize", checkIfMobile);

    // Clean up
    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  // Inject our custom styles for disabled datepicker
  useEffect(() => {
    // Check if style already exists
    if (!document.getElementById("date-picker-disabled-styles")) {
      const styleElement = document.createElement("style");
      styleElement.id = "date-picker-disabled-styles";
      styleElement.innerHTML = datePickerStyles;
      document.head.appendChild(styleElement);

      return () => {
        const existingStyle = document.getElementById(
          "date-picker-disabled-styles"
        );
        if (existingStyle) {
          document.head.removeChild(existingStyle);
        }
      };
    }
  }, []);

  const validateName = (name) => {
    const regex = /^[A-Za-z\s]+$/;
    if (!name) return "*Name is required";
    if (!regex.test(name)) return "*Accept only alphabets";
    return "";
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return "*Email is required";
    if (!regex.test(email)) return "*Invalid email format";
    return "";
  };

  const validatePhone = (phone) => {
    const regex = /^\d{6,14}$/;
    if (!phone) return "*Phone is required";
    if (!regex.test(phone)) return "*Phone should be 6-14 digits";
    return "";
  };

  const validateRemarks = (remarks) => {
    return remarks.trim() ? "" : "";
  };

  const validateDepart = (depart) => {
    if (!depart) return "*Departure is required";
    return "";
  };

  const validateArrive = (arrive) => {
    if (!arrive) return "*Arrival is required";
    return "";
  };

  const validateLeaveDate = (leaveDate) => {
    if (!leaveDate) return "*Leave date is required";
    return "";
  };

  const validateReturnDate = (returnDate) => {
    if (!isOneWay && !returnDate) return "*Return date is required";
    return "";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let error = "";

    switch (name) {
      case "name":
        error = validateName(value);
        break;
      case "email":
        error = validateEmail(value);
        break;
      case "phone":
        error = validatePhone(value);
        break;
      case "remarks":
        error = validateRemarks(value);
        break;
      case "departFrom":
        error = validateDepart(value);
        break;
      case "arriveAt":
        error = validateArrive(value);
        break;
      case "leaveOn":
        error = validateLeaveDate(value);
        break;
      case "returnOn":
        error = validateReturnDate(value);
        break;
      default:
        break;
    }

    setErrors({ ...errors, [name]: error });
  };

  const handleDepartChange = (e) => {
    const value = e.target.value;
    setDepart(value);
    handleInputChange({ target: { name: "departFrom", value } });

    if (value.trim() === "") {
      setDepartSuggestions([]);
      return;
    }

    const filtered = processedAirports
      .filter((airport) => {
        const codeMatch = airport.airportCode.toLowerCase();
        const nameMatch = airport.airportName.toLowerCase();
        const search = value.toLowerCase();

        return (
          codeMatch.startsWith(search) ||
          nameMatch.startsWith(search) ||
          codeMatch.includes(search) ||
          nameMatch.includes(search)
        );
      })
      .slice(0, 10);

    setDepartSuggestions(filtered);
  };

  const handleArriveChange = (e) => {
    const value = e.target.value;
    setArrive(value);
    handleInputChange({ target: { name: "arriveAt", value } });

    if (value.trim() === "") {
      setArriveSuggestions([]);
      return;
    }

    const filtered = processedAirports
      .filter(
        (airport) =>
          airport.airportCode.toLowerCase().includes(value.toLowerCase()) ||
          airport.airportName.toLowerCase().includes(value.toLowerCase())
      )
      .slice(0, 10);

    setArriveSuggestions(filtered);
  };

  const handleAirportSelect = (airport, type) => {
    if (type === "departure") {
      setDepart(airport.airportName);
      setDepartCode(airport.airportCode);
      setDepartSuggestions([]);
      handleInputChange({ target: { name: "departFrom", value: airport.airportName } });
      console.log("Selected departure airport:", airport.airportName, airport.airportCode);
    } else {
      setArrive(airport.airportName);
      setArriveCode(airport.airportCode);
      setArriveSuggestions([]);
      handleInputChange({ target: { name: "arriveAt", value: airport.airportName } });
      console.log("Selected arrival airport:", airport.airportName, airport.airportCode);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData(e.target);
    let data = Object.fromEntries(formData.entries());

    // Add missing state values
    data.departFrom = depart;
    data.arriveAt = arrive;
    data.tripType = isOneWay ? "One Way" : "Return";
    data.leaveOn = leaveDate ? leaveDate.toISOString().split("T")[0] : "";
    data.returnOn = returnDate ? returnDate.toISOString().split("T")[0] : "";
    data.class = formData.get("class") || "";
    data.addHotel = formData.get("addHotel") || "";
    data.tagLine = "Homepage enquiry form";

    // Validate all fields
    const newErrors = {
      name: validateName(data.name),
      email: validateEmail(data.email),
      phone: validatePhone(data.phone),
      remarks: validateRemarks(data.remarks),
      departFrom: validateDepart(data.departFrom),
      arriveAt: validateArrive(data.arriveAt),
      leaveOn: validateLeaveDate(data.leaveOn),
      returnOn: validateReturnDate(data.returnOn),
    };

    setErrors(newErrors);

    // Check for errors
    const hasErrors = Object.values(newErrors).some((error) => error !== "");
    if (hasErrors) {
      return;
    }

    setIsDisabled(true);
    
    // Create a more robust search params object
    const searchData = {
      origin: departCode || "LHR", // Use the stored code or default to LHR
      originName: depart || "London All Airport",
      destination: arriveCode || "",
      destinationName: arrive || "",
      departureDate: leaveDate ? formatDateForStorage(leaveDate) : "",
      returnDate: returnDate ? formatDateForStorage(returnDate) : "",
      travelers: parseInt(document.querySelector('select[name="passengers"]').value) || 1,
      tripType: isOneWay ? "one-way" : "round-trip",
      travelClass: document.querySelector('select[name="class"]').value || "Economy"
    };
    
    function formatDateForStorage(date) {
      const d = new Date(date);
      d.setHours(12, 0, 0, 0);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    
    console.log("Saving search data to session storage:", searchData);
    
    // Save search data to session storage so FlightResult can access it
    sessionStorage.setItem('flightSearchParams', JSON.stringify(searchData));
    
    // Send with EmailJS for notification emails
    emailjs
      .send("service_e0a4ln5", "template_p07mwol", data, "EhCUrSkSnhPlmJ6T-")
      .then((result) => {
        console.log("Email Sent Successfully:", result);
        
        // Replace navigation with SweetAlert
        Swal.fire({
          title: 'Booking Submitted!',
          text: 'Thank you for your booking. We will contact you shortly.',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#3085d6',
        }).then(() => {
          // Optional: Reset form or perform any additional actions
          e.target.reset();
        });
      })
      .catch((error) => {
        console.error("Email Sending Error:", error);
        
        // Show error alert
        Swal.fire({
          title: 'Booking Failed',
          text: 'There was an error submitting your booking. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#d33',
        });
      })
      .finally(() => setIsDisabled(false));
  };

  // Modified custom date picker input with inline styles for disabled state
  const CustomDatePickerInput = ({
    value,
    onClick,
    placeholder,
    isDisabled,
  }) => (
    <input
      type="text"
      value={value}
      onClick={isDisabled ? null : onClick}
      placeholder={placeholder}
      readOnly
      className={`input ${isDisabled ? "disabled-date-input" : ""}`}
      style={
        isDisabled
          ? {
              cursor: "not-allowed",
              opacity: isMobile ? 0.4 : 0.6,
              backgroundColor: "#f5f5f5",
              pointerEvents: "none",
            }
          : {}
      }
    />
  );

  const handleLeaveDateChange = (date) => {
    setLeaveDate(date);
    handleInputChange({ target: { name: "leaveOn", value: date } });

    // Reset return date if it's before the selected leave date
    if (returnDate && returnDate < date) {
      setReturnDate(null);
    }
  };

  const handleReturnDateChange = (date) => {
    setReturnDate(date);
    handleInputChange({ target: { name: "returnOn", value: date } });
  };

  const calladmin = () => {
    window.location.href = "tel:02038852600";
  };
  
  return (
    <div id="h-fh" className="flight-deal-container">
      <div className="full-deal">
        <form className="flight-deal-form" onSubmit={handleSubmit}>
          
          <div className="trip-type">
            <label>
              <input
                type="radio"
                name="tripType"
                checked={!isOneWay}
                onChange={() => setIsOneWay(false)}
              />
              Return
            </label>
            <label>
              <input
                type="radio"
                name="tripType"
                checked={isOneWay}
                onChange={() => setIsOneWay(true)}
              />
              One Way
            </label>
          </div>

          <div className="form-grid">
            <label className="relative">
              Depart from *
              <input
                type="text"
                autoComplete="off"
                className="input"
                value={depart}
                onChange={handleDepartChange}
                onFocus={() => {
                  if (depart === "London All Airport") {
                    setDepart("");
                  }
                }}
                name="departFrom"
                disabled={isLoading}
              />
              {errors.departFrom && (
                <span className="error">{errors.departFrom}</span>
              )}
              {departSuggestions.length > 0 && (
                <ul className="absolute-dropdown bg-dark border rounded shadow-md max-h-48 overflow-y-auto z-10">
                  {departSuggestions.map((airport) => (
                    <li
                      key={airport.id}
                      onClick={() =>
                        handleAirportSelect(airport, "departure")
                      }
                      className="p-2 hover:bg-gray-200 cursor-pointer"
                    >
                      {airport.airportCode} - {airport.airportName}
                    </li>
                  ))}
                </ul>
              )}
            </label>

            <label className="relative">
              Arrive at *
              <input
                type="text"
                autoComplete="off"
                className="input"
                value={arrive}
                onChange={handleArriveChange}
                name="arriveAt"
                disabled={isLoading}
              />
              {errors.arriveAt && (
                <span className="error">{errors.arriveAt}</span>
              )}
              {arriveSuggestions.length > 0 && (
                <ul className="absolute-dropdown bg-dark border rounded shadow-md max-h-48 overflow-y-auto z-10">
                  {arriveSuggestions.map((airport) => (
                    <li
                      key={airport.id}
                      onClick={() =>
                        handleAirportSelect(airport, "arrival")
                      }
                      className="p-2 hover:bg-gray-200 cursor-pointer"
                    >
                      {airport.airportCode} - {airport.airportName}
                    </li>
                  ))}
                </ul>
              )}
            </label>

            <label>
  Leave on *
  <div className="date-picker-container">
    <DatePicker
      selected={leaveDate}
      onChange={handleLeaveDateChange}
      dateFormat="dd/MM/yyyy"
      placeholderText="DD/MM/YYYY"
      name="leaveOn"
      shouldCloseOnSelect={true}
      withPortal={isMobile}
      portalId="leave-date-portal"
      ref={leaveDatePickerRef}
      customInput={
        <CustomDatePickerInput
          placeholder="DD/MM/YYYY"
          isDisabled={false}
        />
      }
      popperClassName="date-picker-popper"
      popperModifiers={{
        preventOverflow: {
          enabled: true,
          escapeWithReference: false,
          boundariesElement: "viewport",
        },
      }}
    />
    <span className="date-picker-icon">📅</span>
  </div>
  {errors.leaveOn && (
    <span className="error">{errors.leaveOn}</span>
  )}
</label>

{!isOneWay && (
  <label>
    Return on *
    <div className="date-picker-container">
      <DatePicker
        selected={returnDate}
        onChange={handleReturnDateChange}
        dateFormat="dd/MM/yyyy"
        placeholderText="DD/MM/YYYY"
        minDate={leaveDate || new Date()}
        disabled={!leaveDate}
        name="returnOn"
        shouldCloseOnSelect={true}
        withPortal={isMobile}
        portalId="return-date-portal"
        ref={returnDatePickerRef}
        customInput={
          <CustomDatePickerInput
            placeholder="DD/MM/YYYY"
            isDisabled={!leaveDate}
          />
        }
        popperClassName="date-picker-popper"
        popperModifiers={{
          preventOverflow: {
            enabled: true,
            escapeWithReference: false,
            boundariesElement: "viewport",
          },
        }}
      />
      <span className="date-picker-icon">📅</span>
    </div>
    {errors.returnOn && (
      <span className="error">{errors.returnOn}</span>
    )}
  </label>
)}

<label>
              Class
              <div className="select-container">
                <select className="input" name="class">
                  {["Economy", "First", "Business", "Premium Economy"].map(
                    (cls) => (
                      <option key={cls}>{cls}</option>
                    )
                  )}
                </select>
                <span className="dropdown-icon">▼</span>
              </div>
            </label>

            <label>
              Passengers
              <div className="select-container">
                <select className="input" name="passengers">
                  {[1, 2, 3, 4].map((num) => (
                    <option key={num}>{num}</option>
                  ))}
                </select>
                <span className="dropdown-icon">▼</span>
              </div>
            </label>

            <label>
              Add hotel?
              <select className="input" name="addHotel">
                {["No", "Yes"].map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </select>
            </label>

            {["Name *", "Email *", "Phone *", "Remarks"].map(
              (placeholder, idx) => (
                <label key={idx}>
                  {placeholder}
                  <input
                    type={
                      placeholder.includes("Email")
                        ? "email"
                        : placeholder.includes("Phone")
                        ? "tel"
                        : "text"
                    }
                    className="input"
                    autoComplete="off"
                    name={placeholder.split(" ")[0].toLowerCase()}
                    onChange={handleInputChange}
                  />
                  {errors[placeholder.split(" ")[0].toLowerCase()] && (
                    <span className="error">
                      {errors[placeholder.split(" ")[0].toLowerCase()]}
                    </span>
                  )}
                </label>
              )
            )}
            <button
              type="submit"
              className="submit-buttonf"
              disabled={isDisabled || isLoading}
            >
              {isDisabled ? "Please wait..." : isLoading ? "Loading..." : "BOOK NOW"}
            </button>
            <button type="button" onClick={calladmin} className="submit-buttonf2">CALL NOW</button>
          </div>
          <div className="but">
           
          
           
          </div>
        </form>
        
        <div className="right-inside">
          <img src="/fli.svg" alt="Description" />
        </div>
      </div>
    </div>
  );
}


